import {
  HttpContext,
  HttpContextToken,
  HttpErrorResponse,
  type HttpInterceptorFn,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, switchMap, throwError } from 'rxjs';
import { KUNDE_TOKEN_SENT, KundeTokenService } from '@comparit/infrastructure';
import { KundeReauthService } from '../services/kunde-reauth.service';

/**
 * Marks a request that must never open the login dialog. Two cases need it: the
 * login call itself, where a 401 means "wrong password" and re-authenticating
 * would clear a perfectly good token and loop; and `/laden`, which owns its own
 * 401 because only it holds the restore token that binds the customer to a
 * claimed task.
 */
export const SKIP_REAUTH = new HttpContextToken(() => false);

/**
 * Retry-once latch, on the context so `clone()` shares it by reference.
 *
 * The retry below cannot re-enter this interceptor — `catchError` does not catch
 * the observable its own handler returns — so this does not guard that path. It
 * guards a *caller* resubscribing the same `HttpRequest` (an outer `retry()`, or
 * a second `subscribe()` on the cold observable `HttpClient` returned), which
 * re-runs the whole chain with this very context.
 */
const REAUTH_ATTEMPTED = new HttpContextToken(() => false);

/** `{ context: skipReauth() }` for generated-client calls that must opt out. */
export function skipReauth(context = new HttpContext()): HttpContext {
  return context.set(SKIP_REAUTH, true);
}

/**
 * Turns a rejected customer session into a login prompt and a retry, so an
 * expired session costs the user one login instead of a lost action (KFZ-2512).
 *
 * Registered *outside* `kundeTokenInterceptor` (see `infrastructureProviders`),
 * which is what makes the retry work: `next(req)` re-enters the token attach and
 * carries the token the dialog just obtained. Replaying is safe because a 401
 * means the server rejected the request before executing it — unlike a 5xx or a
 * timeout, no write can be applied twice.
 */
export const kundeReauthInterceptor: HttpInterceptorFn = (req, next) => {
  const reauth = inject(KundeReauthService);
  const kundeToken = inject(KundeTokenService);

  return next(req).pipe(
    catchError((err: unknown) => {
      if (!shouldReauthenticate(err, req, kundeToken.windowMode())) return throwError(() => err);

      // Latch before re-authenticating so the retry cannot start another round:
      // a 401 on the second attempt is the server refusing a freshly issued
      // token, not a session that expired twice.
      req.context.set(REAUTH_ATTEMPTED, true);

      return reauth
        .reauthenticate()
        .pipe(switchMap(succeeded => (succeeded ? next(req) : throwError(() => err))));
    })
  );
};

function shouldReauthenticate(
  err: unknown,
  req: Parameters<HttpInterceptorFn>[0],
  windowMode: boolean
): boolean {
  if (req.context.get(SKIP_REAUTH) || req.context.get(REAUTH_ATTEMPTED)) return false;

  // Only a session the server actually rejected: without a token attached the
  // caller was never authenticated, and the 401 is theirs to handle.
  if (!req.context.get(KUNDE_TOKEN_SENT) && !windowMode) return false;

  return err instanceof HttpErrorResponse && err.status === 401;
}
